import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { analysisApi } from '@/services/analysisApi';
import DemoExplainer, { InfoTip } from '@/components/DemoExplainer';
import { cn } from '@/lib/utils';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Radio, LayoutGrid, Users, LogOut, Sparkles, BarChart2, Layers, LineChart, Shuffle, GitMerge } from 'lucide-react';
import ScenarioWorkshop from './ScenarioWorkshop';

/* ================================================================
   TOKENS
   ================================================================ */
// Design tokens sourced from tokens.css — use var(--token) in JSX styles
// Colour aliases for JS references (mirrors tokens.css)
const C = {
  bg:       '#0A0A1A',
  bg2:      '#0D0D20',
  bg3:      'rgba(255,255,255,0.03)',
  bg4:      'rgba(255,255,255,0.06)',
  text:     '#FFFFFF',
  t2:       'rgba(255,255,255,0.92)',
  t3:       'rgba(255,255,255,0.72)',
  t4:       'rgba(255,255,255,0.48)',
  line:     'rgba(255,255,255,0.10)',
  lines:    'rgba(255,255,255,0.07)',
  bot:      '#00D4C8',
  borrow:   '#06B6D4',
  build:    '#C9A84C',
  bi:       '#E8B96A',
  buy:      '#EC4899',
  teal:     '#00D4C8',
  gold:     '#C9A84C',
  violet:   '#E8B96A',
  pink:     '#EC4899',
  cyan:     '#06B6D4',
  amber:    '#C9A84C',
  sigAmber: '#F59E0B',
  sigRed:   '#FF4444',
  sigGreen: '#10B981',
  muted:    'rgba(255,255,255,0.48)',
  surface:  '#1A1A2E',
  surface2: '#22223E',
  // ── Retained pre-existing keys (used elsewhere in this file) ──
  card:        '#111B2E',
  border:      'rgba(255,255,255,0.07)',
  borderLight: 'rgba(255,255,255,0.12)',
  white:       '#F2F6FA',
  fog:         '#6B7E91',
  green:       '#2EC8A0',
  red:         '#FF3B5C',
  blue:        '#4A9EFF',
};
const NAV_H = 60;
const DISC_LABELS = {
  operational_excellence: 'Operational Excellence',
  customer_intimacy: 'Customer Intimacy',
  product_leadership: 'Product Leadership',
  growth_capacity: 'Growth Capacity',
};
const SUB_COLORS = {
  ai_addressable: '#7C5CFC', fractional: '#C9A84C', interim: '#FF3B5C',
  advisory: '#4A9EFF', human_strategic: '#00D4C8', inclusion_pathway: '#2EC8A0',
  buy: '#F2F6FA',
};
const SUB_LABELS = {
  ai_addressable: 'BOT', fractional: 'BORROW/Frac', interim: 'BORROW/Int',
  advisory: 'BORROW/Adv', human_strategic: 'BUILD/Strat', inclusion_pathway: 'BUILD/Incl',
  buy: 'BUY',
};

/* ================================================================
   ANIMATED NUMBER
   ================================================================ */
const AnimNum = ({ value, prefix = '', suffix = '', decimals = 0 }) => {
  const [disp, setDisp] = useState(0);
  const prev = useRef(0);
  const raf = useRef(null);
  useEffect(() => {
    const from = prev.current; const to = value; prev.current = to;
    let start = null;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / 800, 1);
      setDisp(from + (to - from) * (1 - Math.pow(1 - p, 4)));
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value]);
  const fmt = decimals > 0 ? disp.toFixed(decimals) : Math.round(disp).toLocaleString();
  return <>{prefix}{fmt}{suffix}</>;
};

/* ================================================================
   TOP NAV (same as Signal Room)
   ================================================================ */
const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const dailyNav = [
    { label: 'Signal Room', icon: Radio, path: '/signal-room' },
    { label: 'Intelligence', icon: BarChart2, path: '/intelligence' },
    { label: 'Blueprint Room', icon: LayoutGrid, path: '/blueprint/cockpit' },
    { label: 'The Stage', icon: Users, path: '/stage' },
    { label: 'Showcase', icon: Sparkles, path: '/showcase/ai-tools' },
  ];
  const flowNav = [
    { label: 'Deconstruct', icon: Layers, path: '/deconstruct' },
    { label: 'Analyze', icon: LineChart, path: '/analyze' },
    { label: 'Sort · 4B', icon: Shuffle, path: '/sort' },
    { label: 'Recompose', icon: GitMerge, path: '/recompose' },
  ];
  const renderNavLink = (item) => {
    const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    return (
      <Link key={item.path} to={item.path}
        className={cn("flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all",
          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}>
        <item.icon className="h-4 w-4" />{item.label}
      </Link>
    );
  };
  return (
    <header style={{ height: NAV_H, position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: 'rgba(3,5,10,0.85)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${C.border}` }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', height: '100%', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <Link to="/signal-room"><Logo size="nav" /></Link>
          <nav style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {dailyNav.map(renderNavLink)}
            <span aria-hidden="true" style={{ display: 'inline-block', width: 1, height: 18, background: 'var(--line)', margin: '0 8px' }} />
            {flowNav.map(renderNavLink)}
          </nav>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,212,200,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.teal }}>{user?.name?.charAt(0) || 'C'}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/login'); }} className="text-muted-foreground">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

/* ================================================================
   KPI CARD
   ================================================================ */
const KpiCard = ({ label, children, sub, note, color }) => (
  <div className="glass" style={{ borderRadius: 12, padding: '24px 20px' }}>
    <p className="eyebrow" style={{ marginBottom: 12 }}>{label}</p>
    <div className="gap-num" style={{ fontSize: 40, color: color || C.white }}>{children}</div>
    {sub && <p style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.fog, marginTop: 8 }}>{sub}</p>}
    {note && <p style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{note}</p>}
  </div>
);

/* ================================================================
   SKELETON
   ================================================================ */
const Skel = ({ w = '100%', h = 24 }) => (
  <div style={{ width: w, height: h, borderRadius: 6, background: 'linear-gradient(90deg,rgba(255,255,255,.04) 25%,rgba(255,255,255,.08) 50%,rgba(255,255,255,.04) 75%)', backgroundSize: '200% 100%', animation: 'skele 1.5s ease-in-out infinite' }} />
);

/* ================================================================
   ROLE TABLE (rendered in roletable tab)
   ================================================================ */
const RoleTable = ({ roles = [], currency = 'GBP', baseWeeks = 12 }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [expandedIdx, setExpandedIdx] = useState(null);

  const botPct = (r) => r.transformation_allocation?.bot_pct || 0;
  const borrowPct = (r) =>
    (r.transformation_allocation?.borrow_fractional_pct || 0) +
    (r.transformation_allocation?.borrow_interim_pct || 0) +
    (r.transformation_allocation?.borrow_advisory_pct || 0);
  const buildPct = (r) =>
    (r.transformation_allocation?.build_human_strategic_pct || 0) +
    (r.transformation_allocation?.build_inclusion_pct || 0);
  const buyPct = (r) => r.transformation_allocation?.buy_pct || 0;
  const timelineWeeks = (r) =>
    Math.round((r.transformation_timeline_multiplier || 1) * baseWeeks);

  const filtered = roles
    .filter((r) => {
      const title = (r.original_title || r.role_title || '').toLowerCase();
      if (search && !title.includes(search.toLowerCase())) return false;
      if (filter === 'filled' && r.filled === false) return false;
      if (filter === 'vacant' && r.filled !== false) return false;
      if (filter === 'highbot' && botPct(r) <= 30) return false;
      if (filter === 'highborrow' && borrowPct(r) <= 25) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'bot') return botPct(b) - botPct(a);
      if (sortBy === 'borrow') return borrowPct(b) - borrowPct(a);
      if (sortBy === 'timeline') return timelineWeeks(a) - timelineWeeks(b);
      return (a.original_title || a.role_title || '').localeCompare(b.original_title || b.role_title || '');
    });

  const avgOf = (fn) => roles.length ? Math.round(roles.reduce((s, r) => s + fn(r), 0) / roles.length) : 0;

  const MiniBar = ({ value, colour, width = 60 }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)' }}>
        <div style={{ width: `${Math.min(value, 100)}%`, height: '100%', borderRadius: 2, background: colour }} />
      </div>
      <span style={{ fontSize: 12, color: colour, fontWeight: 500, minWidth: 32 }}>{value}%</span>
    </div>
  );

  const Pill = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 500,
      cursor: 'pointer', border: active ? `1px solid ${C.teal}` : `1px solid ${C.border}`,
      background: active ? `${C.teal}18` : 'transparent',
      color: active ? C.teal : C.muted,
    }}>{label}</button>
  );

  const TaskPill = ({ t }) => {
    const colMap = { bot: C.violet, borrow: C.gold, build: C.teal, buy: C.white };
    const col = colMap[t.category_4b] || C.muted;
    return (
      <div style={{ padding: '6px 10px', marginBottom: 4, background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 4, background: `${col}15`, color: col, fontFamily: 'var(--fm)', textTransform: 'uppercase' }}>
            {t.sub_label?.replace('_', ' ') || t.category_4b}
          </span>
          <span style={{ fontSize: 12, color: C.white, flex: 1 }}>{t.task}</span>
          <span style={{ fontSize: 10, color: C.muted, fontFamily: 'var(--fm)' }}>{t.estimated_hours || '-'} hrs/yr</span>
        </div>
        {t.category_4b === 'bot' && t.subtask_data?.realistic_automation_pct != null && (
          <div style={{ fontSize: 10, color: C.violet, marginTop: 4, fontFamily: 'var(--fm)' }}>
            AI can handle {t.subtask_data.realistic_automation_pct}% realistically
          </div>
        )}
      </div>
    );
  };

  const renderDrillDown = (role) => {
    const tasks = role.task_breakdown || [];
    const staying = tasks.filter((t) => t.category_4b === 'build' && t.sub_label === 'human_strategic');
    const transforming = tasks.filter((t) => t.category_4b === 'bot' || t.category_4b === 'borrow');
    const redesign = tasks.filter((t) => t.sub_label === 'inclusion_pathway' || t.category_4b === 'buy');
    return (
      <tr>
        <td colSpan={7} style={{ padding: '12px 16px', background: 'rgba(255,255,255,0.03)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <div style={{ fontSize: 10, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontFamily: 'var(--fm)' }}>Staying FTE (Build / Strategic)</div>
              {staying.length > 0 ? staying.map((t, i) => <TaskPill key={i} t={t} />) : <div style={{ fontSize: 11, color: C.muted }}>None</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, color: C.violet, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontFamily: 'var(--fm)' }}>Transforming (Bot + Borrow)</div>
              {transforming.length > 0 ? transforming.map((t, i) => <TaskPill key={i} t={t} />) : <div style={{ fontSize: 11, color: C.muted }}>None</div>}
            </div>
            <div>
              <div style={{ fontSize: 10, color: C.green, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, fontFamily: 'var(--fm)' }}>Redesign (Inclusion + Buy)</div>
              {redesign.length > 0 ? redesign.map((t, i) => <TaskPill key={i} t={t} />) : <div style={{ fontSize: 11, color: C.muted }}>None</div>}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const clearFilters = () => { setSearch(''); setFilter('all'); setSortBy('name'); };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Summary bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[
          { label: `${roles.length} roles`, color: C.white },
          { label: `${avgOf(botPct)}% Bot avg`, color: 'var(--bot)' },
          { label: `${avgOf(borrowPct)}% Borrow avg`, color: 'var(--borrow)' },
          { label: `${avgOf(buildPct)}% Build avg`, color: 'var(--build)' },
        ].map((m) => (
          <div key={m.label} style={{ padding: '6px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, fontSize: 12, color: m.color, fontWeight: 500 }}>
            {m.label}
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search roles..."
          style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)', color: C.white, fontSize: 12, outline: 'none', width: 180 }}
        />
        {[
          { key: 'all', label: 'All' },
          { key: 'filled', label: 'Filled' },
          { key: 'vacant', label: 'Vacant' },
          { key: 'highbot', label: 'High Bot' },
          { key: 'highborrow', label: 'High Borrow' },
        ].map((f) => (
          <Pill key={f.key} label={f.label} active={filter === f.key} onClick={() => setFilter(f.key)} />
        ))}
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
          style={{ marginLeft: 'auto', padding: '6px 10px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.card, color: C.muted, fontSize: 11, outline: 'none' }}>
          <option value="name">Role name</option>
          <option value="bot">Bot %</option>
          <option value="borrow">Borrow %</option>
          <option value="timeline">Timeline</option>
        </select>
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ overflowX: typeof window !== 'undefined' && window.innerWidth < 768 ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: typeof window !== 'undefined' && window.innerWidth < 768 ? 800 : 'unset' }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
              {['Role', 'Bot', 'Borrow', 'Build', 'Buy', 'Timeline', 'Actions'].map((h) => (
                <th key={h} style={{
                  padding: '10px 16px', textAlign: h === 'Role' ? 'left' : 'center',
                  fontSize: 11, color: 'var(--t4)', textTransform: 'uppercase',
                  letterSpacing: '0.06em', fontWeight: 500, position: 'sticky', top: 0,
                  background: 'rgba(255,255,255,0.05)',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ color: C.muted, fontSize: 13, marginBottom: 12 }}>No roles match your filters</div>
                  <button onClick={clearFilters} style={{ padding: '6px 16px', borderRadius: 8, border: `1px solid ${C.teal}`, background: 'transparent', color: C.teal, fontSize: 12, cursor: 'pointer' }}>Clear filters</button>
                </td>
              </tr>
            ) : filtered.map((role, idx) => {
              const isExpanded = expandedIdx === idx;
              const isFilled = role.filled !== false;
              const alloc = role.transformation_allocation || {};
              return (
                <React.Fragment key={idx}>
                  <tr
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', background: isExpanded ? 'rgba(0,212,200,0.03)' : 'transparent', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{role.original_title || role.role_title}</div>
                      <span style={{
                        fontSize: 9, padding: '1px 6px', borderRadius: 4, marginTop: 4, display: 'inline-block',
                        fontFamily: 'var(--fm)',
                        background: isFilled ? 'rgba(255,255,255,0.06)' : `${C.teal}18`,
                        color: isFilled ? C.muted : C.teal,
                      }}>{isFilled ? 'Filled' : 'Vacant'}</span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}><MiniBar value={botPct(role)} colour="var(--bot)" /></td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }} title={`Fractional ${alloc.borrow_fractional_pct || 0}% / Interim ${alloc.borrow_interim_pct || 0}% / Advisory ${alloc.borrow_advisory_pct || 0}%`}>
                      <MiniBar value={borrowPct(role)} colour="var(--borrow)" />
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }} title={`Human Strategic ${alloc.build_human_strategic_pct || 0}% / Inclusion ${alloc.build_inclusion_pct || 0}%`}>
                      <MiniBar value={buildPct(role)} colour="var(--build)" />
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}><MiniBar value={buyPct(role)} colour="var(--buy)" /></td>
                    <td style={{ padding: '10px 16px', textAlign: 'center', fontFamily: 'var(--fm)', fontSize: 12, color: C.muted }}>{timelineWeeks(role)}w</td>
                    <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        {(borrowPct(role) > 0 || buyPct(role) > 0) && (
                          <button onClick={(e) => { e.stopPropagation(); navigate('/stage'); }}
                            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.gold}`, background: 'transparent', color: C.gold, cursor: 'pointer' }}>
                            → Stage
                          </button>
                        )}
                        {botPct(role) > 0 && (
                          <button onClick={(e) => {
                            e.stopPropagation();
                            const botTasks = (role.task_breakdown || []).filter((t) => t.category_4b === 'bot');
                            const botThemes = botTasks.map((t) => t.task.toLowerCase());
                            const subLabels = botTasks.map((t) => t.sub_label?.replace(/_/g, ' ')).filter(Boolean);
                            const allThemes = [...new Set([...botThemes, ...subLabels])];
                            localStorage.setItem('reai_showcase_context', JSON.stringify({
                              session_id: localStorage.getItem('session_id'),
                              function: role.original_title || role.role_title,
                              bot_task_themes: allThemes,
                              bot_task_count: botTasks.length,
                              roles: [role.original_title || role.role_title],
                              role_filter: role.original_title || role.role_title,
                            }));
                            localStorage.setItem('showcase_return_url', '/blueprint/cockpit?tab=roletable');
                            navigate('/showcase/ai-tools?role=' + encodeURIComponent(role.original_title || role.role_title));
                          }}
                            style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: `1px solid ${C.violet}`, background: 'transparent', color: C.violet, cursor: 'pointer' }}>
                            → Showcase
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && renderDrillDown(role)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
const BlueprintCockpit = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [blueprint, setBlueprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRole, setExpandedRole] = useState(null);
  const [activatingStage, setActivatingStage] = useState(false);
  const [successionRole, setSuccessionRole] = useState(null);
  const [exportingPptx, setExportingPptx] = useState(false); // { role, path: 'internal'|'external'|null }
  const [orgIntelData, setOrgIntelData] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);

  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const functionParam = searchParams.get('function');
  const [activeTab, setActiveTab] = useState(tabParam || 'overview');

  useEffect(() => {
    // Inject skeleton animation
    if (!document.getElementById('bp-kf')) {
      const s = document.createElement('style');
      s.id = 'bp-kf';
      s.textContent = '@keyframes skele{0%{background-position:-200% 0}100%{background-position:200% 0}}';
      document.head.appendChild(s);
    }

    (async () => {
      try {
        const sid = localStorage.getItem('reai_session_id') || localStorage.getItem('analysis_session_id');
        if (!sid) { navigate('/analysis'); return; }

        const res = await analysisApi.getSession(sid);
        const sess = res.data?.session;
        if (!sess) { navigate('/analysis'); return; }
        setSession(sess);

        // Blueprint data from localStorage or session
        const cached = localStorage.getItem('analysis_blueprint');
        if (cached) {
          setBlueprint(JSON.parse(cached));
        } else if (sess.blueprint_id) {
          // Blueprint kpis might be on the session
          setBlueprint({ kpis: sess.kpis || null, role_table: sess.role_table || null, blueprint_id: sess.blueprint_id });
        }
      } catch {
        navigate('/analysis');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  useEffect(() => {
    const SIGNAL_HEURISTIC = {
      red:   { bot_pct: 38, borrow_pct: 22, build_pct: 28, buy_pct: 12 },
      amber: { bot_pct: 25, borrow_pct: 28, build_pct: 33, buy_pct: 14 },
      green: { bot_pct: 15, borrow_pct: 30, build_pct: 40, buy_pct: 15 },
    };
    const slugify = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, '_');
    const enrich = (fns, currency) => fns.map((fn) => {
      const heur = SIGNAL_HEURISTIC[fn.signal] || SIGNAL_HEURISTIC.green;
      const gap = fn.gap || 0;
      const labour_cost = fn.labour_cost ?? gap * 3.2;
      const avgAnnualCostPerPerson =
        currency === 'INR' ? 800000 :
        currency === 'AED' ? 120000 :
        currency === 'USD' ? 80000 :
        50000;
      const headcount = fn.headcount || Math.round((gap * 3.2) / avgAnnualCostPerPerson);
      return {
        ...fn,
        function_id: fn.function_id || slugify(fn.name),
        bot_pct: fn.bot_pct ?? heur.bot_pct,
        borrow_pct: fn.borrow_pct ?? heur.borrow_pct,
        build_pct: fn.build_pct ?? heur.build_pct,
        buy_pct: fn.buy_pct ?? heur.buy_pct,
        labour_cost,
        headcount,
        gap,
        signal: fn.signal || 'green',
      };
    });

    const isDemo = localStorage.getItem('is_demo') === 'true';
    if (isDemo) {
      try {
        const raw = JSON.parse(localStorage.getItem('demo_signal_data') || '{}');
        setOrgIntelData({
          org_name: raw.org_name || 'Demo Organisation',
          currency: raw.currency || 'INR',
          functions: enrich(raw.functions || [], raw.currency || 'INR'),
        });
      } catch (e) {
        setOrgIntelData({ functions: [], currency: 'INR' });
      }
      return;
    }

    const token = localStorage.getItem('token');
    const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    fetch(`${API_BASE}/api/chro/org-intelligence`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        setOrgIntelData({
          ...data,
          functions: enrich(data.functions || [], data.currency || 'INR'),
        });
      })
      .catch(() => {
        setOrgIntelData({ functions: [], currency: 'INR' });
      });
  }, []);

  // Compute KPIs from session data if blueprint kpis missing
  const roles = session?.molecularisation_results || [];
  const func = session?.function || '';
  const discipline = session?.value_discipline || '';
  const currSym = { GBP: '£', USD: '$', AED: 'AED ', INR: '₹', MUR: 'Rs ' }[localStorage.getItem('demo_currency') || session?.currency || 'GBP'] || '£';
  const computeKpis = () => {
    // Always compute capacity from task data for accuracy
    let capHrs = 0;
    let realisticHrs = 0;
    roles.forEach((r) => {
      (r.task_breakdown || []).forEach((t) => {
        if ((t.category_4b === 'bot' || t.category_4b === 'borrow') && (t.estimated_hours || 0) > 0) {
          capHrs += t.estimated_hours;
          // Use realistic automation % if subtask data exists, else assume 60% for bot, 100% for borrow
          if (t.subtask_data?.realistic_automation_pct != null) {
            realisticHrs += t.estimated_hours * (t.subtask_data.realistic_automation_pct / 100);
          } else if (t.category_4b === 'bot') {
            realisticHrs += t.estimated_hours * 0.6;
          } else {
            realisticHrs += t.estimated_hours;
          }
        }
      });
    });
    // Use blueprint capacity if computed value is 0 but blueprint has data
    if (capHrs === 0 && blueprint?.kpis?.capacity_created_hours) {
      capHrs = blueprint.kpis.capacity_created_hours;
    }
    const costSaving = Math.round(capHrs * 45 * 0.6);
    const growthPct = roles.length > 0 ? Math.round(capHrs / (roles.length * 2080) * 100) : 0;
    const weeks = roles.length <= 3 ? 12 : roles.length <= 6 ? 18 : 24;
    const bpKpis = blueprint?.kpis || {};
    return {
      capacity_created_hours: capHrs || bpKpis.capacity_created_hours || 0,
      realistic_capacity_hours: realisticHrs || Math.round((capHrs || 0) * 0.6),
      cost_implication: -costSaving,
      growth_profitability_pct: growthPct || bpKpis.growth_profitability_pct || 0,
      timeline_weeks: bpKpis.timeline_weeks || { min: weeks, max: weeks + 4 },
      roles_included: roles.length || bpKpis.roles_included || 0,
      roles_total: roles.length || bpKpis.roles_total || 0,
    };
  };
  const kpis = computeKpis();
  const fteEquiv = (kpis.capacity_created_hours / 2080).toFixed(1);
  const realisticFte = (kpis.realistic_capacity_hours / 2080).toFixed(1);
  const timelineDisplay = typeof kpis.timeline_weeks === 'object' ? `${kpis.timeline_weeks.min}-${kpis.timeline_weeks.max}` : kpis.timeline_weeks;
  const costAbs = Math.abs(kpis.cost_implication);
  const costDisplay = costAbs >= 1000000 ? `${(costAbs / 1000000).toFixed(1)}M` : costAbs >= 1000 ? `${Math.round(costAbs / 1000)}K` : `${costAbs}`;

  // Role table
  const roleTable = blueprint?.role_table || roles.map((r) => ({ ...r, included: true }));

  const handleActivateStage = async () => {
    setActivatingStage(true);
    try {
      const sid = localStorage.getItem('reai_session_id');
      if (!sid) { toast.error('No session found'); return; }

      // Fetch fresh session data to ensure we have molecularisation_results
      const sessRes = await analysisApi.getSession(sid);
      const sess = sessRes.data?.session;
      if (!sess?.molecularisation_results) {
        toast.error('No molecularisation results found. Run analysis first.');
        return;
      }

      // Build roles_to_activate from BORROW tasks
      const rolesToActivate = [];
      sess.molecularisation_results.forEach((role) => {
        const taskList = role.task_breakdown || role.tasks || [];
        const borrowTasks = taskList.filter((t) =>
          t.category_4b === 'borrow' ||
          t.sub_label === 'fractional' || t.sub_label === 'interim' || t.sub_label === 'advisory' ||
          (t.borrow_score || 0) > 0.6
        );

        if (borrowTasks.length > 0) {
          const interimN = borrowTasks.filter((t) => t.sub_label === 'interim').length;
          const fracN = borrowTasks.filter((t) => t.sub_label === 'fractional').length;
          const advN = borrowTasks.filter((t) => t.sub_label === 'advisory').length;
          const primaryType = interimN >= fracN && interimN >= advN ? 'interim'
            : fracN >= advN ? 'fractional' : 'advisory';

          rolesToActivate.push({
            role_title: role.original_title || role.role_title,
            function: sess.function || func,
            workforce_type: primaryType,
            borrow_tasks: borrowTasks.map((t) => t.task),
            filled: role.filled !== false,
            onet_code: role.onet_code || '',
          });
        }
      });

      console.log('[ActivateStage] rolesToActivate:', rolesToActivate);

      if (rolesToActivate.length === 0) {
        toast.info('No Fractional/Interim/Advisory tasks identified. Ensure BORROW tasks are selected in analysis.');
        return;
      }

      const res = await analysisApi.activateStage(sid, rolesToActivate);
      
      // For demo users, store searches in localStorage
      if (localStorage.getItem('is_demo') === 'true' && res.data.search_ids) {
        const demoSearches = rolesToActivate.map((role, i) => ({
          id: res.data.search_ids[i] || `demo-search-${i}`,
          role_title: role.role_title,
          role_function: role.function,
          workforce_type: role.workforce_type,
          source: 'blueprint_analysis',
          status: 'market_map',
          market_map_count: 0,
          blueprint_tasks: role.borrow_tasks || [],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }));
        localStorage.setItem('demo_searches', JSON.stringify(demoSearches));
      }
      
      toast.success(`${res.data.searches_created} searches created in Stage`);
      navigate('/stage?source=blueprint');
    } catch (err) {
      console.error('Activate stage failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to activate Stage');
    } finally {
      setActivatingStage(false);
    }
  };

  const handleOpenShowcase = () => {
    // Extract BOT tasks and store context for Showcase
    const botTasks = [];
    const roleNames = [];
    const subLabels = new Set();
    roles.forEach((r) => {
      const bots = (r.task_breakdown || []).filter((t) => t.category_4b === 'bot');
      if (bots.length > 0) {
        roleNames.push(r.original_title || r.role_title);
        bots.forEach((t) => {
          botTasks.push(t.task);
          if (t.sub_label) subLabels.add(t.sub_label.replace(/_/g, ' '));
        });
      }
    });
    const themes = [...new Set([...botTasks.map((t) => t.toLowerCase()), ...subLabels])];
    localStorage.setItem('reai_showcase_context', JSON.stringify({
      session_id: localStorage.getItem('session_id') || localStorage.getItem('reai_session_id'),
      function: func,
      bot_task_themes: themes,
      bot_task_count: botTasks.length,
      roles: roleNames,
    }));
    localStorage.setItem('showcase_return_url', '/blueprint/cockpit?tab=overview');
    navigate('/showcase/ai-tools');
  };

  // Pct cell renderer
  const PctCell = ({ value, color }) => {
    const bg = value > 40 ? `${color}30` : value > 20 ? `${color}18` : 'transparent';
    return (
      <td style={{ padding: '10px 12px', textAlign: 'center', background: bg, fontFamily: 'var(--fm)', fontSize: 13, color: value > 0 ? color : C.fog }}>
        {value > 0 ? `${value}%` : '-'}
      </td>
    );
  };

  const handleExportPptx = async () => {
    const isDemo = localStorage.getItem('is_demo') === 'true';
    const sid = localStorage.getItem('analysis_session_id') || (isDemo ? 'demo' : null);
    if (!sid) { toast.error('No active session found'); return; }
    setExportingPptx(true);
    try {
      const token = localStorage.getItem('token');
      const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const resp = await fetch(`${API}/api/chro/blueprint/${sid}/export-pptx`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${localStorage.getItem('demo_org_name') || 'Blueprint'}_Transformation.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Presentation downloaded!');
    } catch (e) {
      toast.error('Export failed. Please try again.');
    }
    setExportingPptx(false);
  };

  // Task drill-down
  const renderDrillDown = (role) => {
    const tasks = role.task_breakdown || [];
    const buildTasks = tasks.filter((t) => t.category_4b === 'build' && t.sub_label === 'human_strategic');
    const transformTasks = tasks.filter((t) => t.category_4b === 'bot' || t.category_4b === 'borrow');
    const redesignTasks = tasks.filter((t) => (t.category_4b === 'build' && t.sub_label === 'inclusion_pathway') || t.category_4b === 'buy');

    const TaskMini = ({ t }) => (
      <div style={{ padding: '8px 10px', marginBottom: 6, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 12, color: C.white, lineHeight: 1.4 }}>{t.task}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <span style={{ fontFamily: 'var(--fm)', fontSize: 9, padding: '1px 6px', borderRadius: 4, background: `${SUB_COLORS[t.sub_label] || C.fog}15`, color: SUB_COLORS[t.sub_label] || C.fog }}>
            {SUB_LABELS[t.sub_label] || t.sub_label}
          </span>
          <span style={{ fontFamily: 'var(--fm)', fontSize: 9, color: C.muted }}>{t.estimated_hours || '-'} hrs</span>
          {t.category_4b === 'borrow' && <span style={{ fontSize: 9, color: C.gold, cursor: 'pointer' }} onClick={() => navigate('/stage')}>Activate in Stage</span>}
          {t.category_4b === 'bot' && <span style={{ fontSize: 9, color: C.violet, cursor: 'pointer' }} onClick={() => { localStorage.setItem('showcase_return_url', '/blueprint/cockpit?tab=overview'); navigate('/showcase/ai-tools'); }}>Find in Showcase</span>}
        </div>
      </div>
    );

    return (
      <tr>
        <td colSpan={7} style={{ padding: '16px 12px', background: `${C.surface}80` }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div>
              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.teal, marginBottom: 8, textTransform: 'uppercase' }}>Staying FTE (BUILD / Strategic)</p>
              {buildTasks.length > 0 ? buildTasks.map((t, i) => <TaskMini key={i} t={t} />) : <p style={{ fontSize: 11, color: C.fog }}>None</p>}
              {buildTasks.length > 0 && (
                <button onClick={() => setSuccessionRole({ role, path: null })} style={{
                  marginTop: 10, width: '100%', padding: '8px 12px', fontSize: 11, fontWeight: 600,
                  background: 'none', border: `1px solid ${C.teal}`, borderRadius: 8,
                  color: C.teal, cursor: 'pointer', letterSpacing: '0.04em',
                }}>
                  Plan Succession →
                </button>
              )}
            </div>
            <div>
              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.violet, marginBottom: 8, textTransform: 'uppercase' }}>Transforming (BOT + BORROW)</p>
              {transformTasks.length > 0 ? transformTasks.map((t, i) => <TaskMini key={i} t={t} />) : <p style={{ fontSize: 11, color: C.fog }}>None</p>}
            </div>
            <div>
              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.green, marginBottom: 8, textTransform: 'uppercase' }}>Redesign (Inclusion + BUY)</p>
              {redesignTasks.length > 0 ? redesignTasks.map((t, i) => <TaskMini key={i} t={t} />) : <p style={{ fontSize: 11, color: C.fog }}>None</p>}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  /* ── SUCCESSION PLANNING MODAL ─────────────────────── */
  const renderSuccessionModal = () => {
    if (!successionRole) return null;
    const { role, path } = successionRole;
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }} onClick={() => setSuccessionRole(null)}>
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
          padding: 32, maxWidth: 560, width: '100%',
        }} onClick={e => e.stopPropagation()}>
          <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Succession Planning</p>
          <p style={{ fontFamily: "'Instrument Serif', serif", fontSize: 22, color: C.white, marginBottom: 6 }}>{role.original_title || role.role_title}</p>
          <p style={{ fontSize: 13, color: C.muted, marginBottom: 24, lineHeight: 1.6 }}>
            This role has been redesigned. The BUILD tasks require human strategic capability.
            How would you like to identify successors?
          </p>

          {!path && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button onClick={() => setSuccessionRole({ role, path: 'internal' })} style={{
                padding: '16px 20px', borderRadius: 12, border: `1px solid ${C.teal}`,
                background: `${C.teal}10`, color: C.white, cursor: 'pointer', textAlign: 'left',
              }}>
                <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.teal, marginBottom: 6, textTransform: 'uppercase' }}>Internal</p>
                <p style={{ fontSize: 13, lineHeight: 1.5 }}>Identify current employees who could step into this redesigned role</p>
              </button>
              <button onClick={() => {
                setSuccessionRole(null);
                navigate('/stage?source=succession&role=' + encodeURIComponent(role.role_title));
              }} style={{
                padding: '16px 20px', borderRadius: 12, border: `1px solid ${C.gold}`,
                background: `${C.gold}10`, color: C.white, cursor: 'pointer', textAlign: 'left',
              }}>
                <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.gold, marginBottom: 6, textTransform: 'uppercase' }}>External</p>
                <p style={{ fontSize: 13, lineHeight: 1.5 }}>Launch a BUILD succession search in The Stage</p>
              </button>
            </div>
          )}

          {path === 'internal' && (
            <div>
              <p style={{ fontFamily: 'var(--fm)', fontSize: 10, color: C.teal, textTransform: 'uppercase', marginBottom: 12 }}>Internal Succession Criteria</p>
              <div style={{ background: C.card, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 12 }}>Based on the BUILD tasks for this role, look for employees with:</p>
                {(role.task_breakdown || []).filter(t => t.category_4b === 'build' && t.sub_label === 'human_strategic').slice(0, 4).map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <span style={{ color: C.teal, flexShrink: 0 }}>→</span>
                    <p style={{ fontSize: 12, color: C.white, lineHeight: 1.4 }}>{t.task}</p>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
                Run this criteria through your HRIS or talent management system. 
                Candidates who match 3+ of these task capabilities are succession-ready.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setSuccessionRole({ role, path: null })} style={{
                  flex: 1, padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`,
                  background: 'none', color: C.muted, cursor: 'pointer', fontSize: 12,
                }}>← Back</button>
                <button onClick={() => setSuccessionRole(null)} style={{
                  flex: 2, padding: '10px', borderRadius: 8, border: 'none',
                  background: C.teal, color: C.bg, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}>Save Succession Plan</button>
              </div>
            </div>
          )}

          <button onClick={() => setSuccessionRole(null)} style={{
            marginTop: 16, width: '100%', padding: '8px', borderRadius: 8,
            background: 'none', border: 'none', color: C.fog, cursor: 'pointer', fontSize: 12,
          }}>Cancel</button>
        </div>
      </div>
    );
  };

  /* ── RENDER ──────────────────────────────────────────── */
  return (
  <React.Fragment>
    <div style={{ background: C.bg, color: C.white, fontFamily: "'Inter', sans-serif", minHeight: '100vh' }}>
      <TopNav />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: `${NAV_H + 40}px 24px 120px` }}>

        {/* Empty state */}
        {!loading && roles.length === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <p style={{ fontFamily: 'var(--fg)', fontSize: 28, color: C.white, marginBottom: 12 }}>Your Blueprint is being prepared</p>
            <p style={{ color: C.muted, fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>Go back to Analysis Results and click Generate Blueprint to complete your Blueprint.</p>
            <button onClick={() => navigate('/analysis/results')} style={{ padding: '12px 24px', fontSize: 14, fontWeight: 600, background: C.gold, color: C.bg, border: 'none', borderRadius: 8, cursor: 'pointer' }}>Back to Analysis</button>
          </div>
        )}

        {roles.length > 0 && (<>
        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
          <span style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.teal }}>Step 4 of 4 - Complete</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{ width: 32, height: 4, borderRadius: 2, background: C.teal }} />
            ))}
          </div>
        </div>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: 'clamp(28px, 4.5vw, 42px)', fontWeight: 700, color: C.white, letterSpacing: '-0.01em', marginBottom: 6, textTransform: 'none' }}>
              Your Transformation Blueprint
            </h1>
            <p style={{ fontFamily: 'var(--fm)', fontSize: 12, color: C.muted }}>
              {func} {discipline ? ` · ${DISC_LABELS[discipline] || discipline}` : ''} · {roles.length} roles {session?.created_at ? ` · Generated ${new Date(session.updated_at || session.created_at).toLocaleDateString()}` : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, background: 'transparent', border: `1px solid ${C.violet}`, borderRadius: 8, color: C.violet, cursor: exportingPptx ? 'wait' : 'pointer', opacity: exportingPptx ? 0.7 : 1 }}
              onClick={handleExportPptx} disabled={exportingPptx}>
              {exportingPptx ? 'Generating...' : '↓ Export as Presentation'}
            </button>
            <button style={{ padding: '8px 16px', fontSize: 12, fontWeight: 500, background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: 'pointer' }}
              onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied'); }}>
              Share Blueprint
            </button>
            <button style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, background: C.gold, border: 'none', borderRadius: 8, color: C.bg, cursor: 'pointer' }}
              onClick={handleActivateStage} disabled={activatingStage}>
              {activatingStage ? 'Activating...' : 'Activate Stage \u2192'}
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 0, borderBottom: `0.5px solid ${C.border}`, marginBottom: 24 }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'scenarios', label: 'Scenarios' },
            { key: 'roletable', label: 'Role Table' },
          ].map((t) => (
            <button
              key={t.key}
              className={`ct${activeTab === t.key ? ' on' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (<>
        {/* KPI cards */}
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 40 }}>
            {[1, 2, 3, 4].map((i) => <div key={i} style={{ background: C.card, borderRadius: 12, padding: 24 }}><Skel w="60%" h={14} /><div style={{ height: 12 }} /><Skel w="80%" h={36} /></div>)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 40 }}>
            <KpiCard label={<><span style={{textTransform:"none",letterSpacing:"normal"}}>Capacity Created <InfoTip position="bottom" width={260} title="Capacity Created" text="Total hours freed across all analysed roles by automating BOT tasks and sourcing BORROW roles externally. This is the raw potential before redeployment decisions." /></span></>} color={C.teal} sub={`${fteEquiv} FTE theoretical · ${realisticFte} FTE realistic`}>
              <AnimNum value={kpis.capacity_created_hours} suffix=" hrs/yr" />
            </KpiCard>
            <KpiCard label={<><span style={{textTransform:"none",letterSpacing:"normal"}}>Cost Implication <InfoTip position="bottom" width={270} title="Cost Implication" text="The net financial impact of acting on this blueprint - cost of fractional/interim sourcing minus savings from automation. A negative number means net saving." /></span></>} color={C.green} sub="net transformation saving">
              -{currSym}{costDisplay}
            </KpiCard>
            <KpiCard label={<><span style={{textTransform:"none",letterSpacing:"normal"}}>Growth Potential <InfoTip position="bottom" width={260} title="Growth Potential" text="Capacity freed by transformation that can be redirected to growth activities - new revenue initiatives, capability building, or strategic projects currently deprioritised due to operational load." /></span></>} color={C.gold} sub="revenue per FTE improvement">
              <AnimNum value={kpis.growth_profitability_pct} prefix="+" suffix="%" />
            </KpiCard>
            <KpiCard label="Transformation Timeline" color={C.violet} sub="to full implementation">
              {timelineDisplay} weeks
            </KpiCard>
          </div>
        )}

        {/* Org Intelligence link */}
        <div style={{ marginBottom: 32 }}>
          <button
            onClick={() => navigate('/intelligence')}
            style={{
              padding: '10px 20px', fontSize: 13, fontWeight: 500,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8,
              color: C.white,
              cursor: 'pointer',
              fontFamily: "'Inter', sans-serif",
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
          >
            View function intelligence &rarr;
          </button>
          <p style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.muted, marginTop: 8 }}>
            See your {func || 'function'} mapped by Bot &middot; Borrow &middot; Build &middot; Buy
          </p>
        </div>

        {/* Role table */}
        <div className="glass" style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 40 }}>
          <div style={{ padding: '20px 20px 12px' }}>
            <h2 style={{ fontFamily: 'var(--fg)', fontSize: 22, color: C.white, marginBottom: 4 }}>Role Transformation Architecture</h2>
            <p style={{ fontFamily: 'var(--fm)', fontSize: 11, color: C.muted }}>Click any role to see task-level detail</p>
          </div>

          <div style={{ overflowX: isMobile ? 'auto' : 'visible', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? 700 : 'unset' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Role', 'Current', 'BOT', 'BORROW', 'BUILD', 'BUY', 'Timeline'].map((h, i) => (
                  <th key={h} style={{
                    padding: '10px 12px', textAlign: i === 0 || i === 1 ? 'left' : 'center',
                    fontFamily: 'var(--fm)', fontSize: 10, color: C.muted,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    borderBottom: `1px solid ${C.border}`,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {roleTable.map((role, idx) => {
                const alloc = role.transformation_allocation || {};
                const botPct = alloc.bot_pct || 0;
                const borrowPct = (alloc.borrow_fractional_pct || 0) + (alloc.borrow_interim_pct || 0) + (alloc.borrow_advisory_pct || 0);
                const buildPct = (alloc.build_human_strategic_pct || 0) + (alloc.build_inclusion_pct || 0);
                const buyPct = alloc.buy_pct || 0;
                const isFilled = role.filled !== false;
                const isExpanded = expandedRole === idx;
                const timeline = Math.round((role.transformation_timeline_multiplier || 1) * (roles.length <= 3 ? 12 : roles.length <= 6 ? 18 : 24));

                return (
                  <React.Fragment key={idx}>
                    <tr
                      onClick={() => setExpandedRole(isExpanded ? null : idx)}
                      style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer', background: isExpanded ? `${C.teal}08` : 'transparent', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.background = `${C.white}05`; }}
                      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '12px 12px' }}>
                        <p style={{ fontSize: 14, fontWeight: 500, color: C.white }}>{role.original_title || role.role_title}</p>
                        <span style={{
                          fontFamily: 'var(--fm)', fontSize: 9, padding: '2px 6px',
                          borderRadius: 4, background: isFilled ? 'rgba(255,255,255,0.06)' : `${C.teal}18`,
                          color: isFilled ? C.muted : C.teal,
                        }}>
                          {isFilled ? 'Filled' : 'Vacant'}
                        </span>
                      </td>
                      <td style={{ padding: '12px 12px', fontFamily: 'var(--fm)', fontSize: 11, color: C.muted }}>
                        FTE
                      </td>
                      <PctCell value={botPct} color={C.violet} />
                      <PctCell value={borrowPct} color={C.gold} />
                      <PctCell value={buildPct} color={C.teal} />
                      <PctCell value={buyPct} color={C.white} />
                      <td style={{ padding: '12px 12px', textAlign: 'center', fontFamily: 'var(--fm)', fontSize: 12, color: C.muted }}>
                        {timeline}w
                      </td>
                    </tr>
                    {isExpanded && renderDrillDown(role)}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        </>)}

        {activeTab === 'scenarios' && (
          <ScenarioWorkshop
            orgFunctions={orgIntelData?.functions || []}
            currency={orgIntelData?.currency || localStorage.getItem('demo_currency') || session?.currency || 'INR'}
            initialFunction={functionParam}
          />
        )}

        {activeTab === 'roletable' && (
          <RoleTable
            roles={roleTable}
            currency={localStorage.getItem('demo_currency') || session?.currency || 'GBP'}
            baseWeeks={roles.length <= 3 ? 12 : roles.length <= 6 ? 18 : 24}
          />
        )}
      </>)}
      </div>

      {/* Fixed bottom bar */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: C.surface, borderTop: `1px solid ${C.border}`,
        padding: '14px 24px', zIndex: 100,
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? 10 : 0 }}>
          <span style={{ fontFamily: 'var(--fm)', fontSize: isMobile ? 11 : 12, color: C.muted, textAlign: isMobile ? 'center' : 'left' }}>
            <strong style={{ color: C.teal }}>{roles.length}</strong> roles &middot; <strong style={{ color: C.teal }}>{kpis.capacity_created_hours.toLocaleString()}</strong> hrs theoretical &middot; <strong style={{ color: C.amber }}>{Math.round(kpis.realistic_capacity_hours).toLocaleString()}</strong> hrs realistic &middot; <strong style={{ color: C.green }}>-{currSym}{costDisplay}</strong> impact
          </span>
          <div style={{ display: 'flex', gap: 8, justifyContent: isMobile ? 'center' : 'flex-end', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/analysis/results')} style={{
              padding: '10px 20px', fontSize: 13, fontWeight: 500,
              background: 'transparent', border: `1px solid ${C.border}`, borderRadius: 8,
              color: C.muted, cursor: 'pointer',
            }}>
              Revise Analysis
            </button>
            <button onClick={handleActivateStage} disabled={activatingStage} style={{
              padding: '10px 20px', fontSize: 13, fontWeight: 600,
              background: C.gold, border: 'none', borderRadius: 8,
              color: C.bg, cursor: 'pointer', opacity: activatingStage ? 0.6 : 1,
            }}>
              {activatingStage ? 'Activating...' : 'Activate Stage \u2192'}
            </button>
            <button onClick={handleOpenShowcase} style={{
              padding: '10px 20px', fontSize: 13, fontWeight: 600,
              background: C.violet, border: 'none', borderRadius: 8,
              color: C.white, cursor: 'pointer',
            }}>
              Open Showcase &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
    {renderSuccessionModal()}
  </React.Fragment>
  );
};

const BlueprintCockpitWithExplainer = (props) => (<><BlueprintCockpit {...props} /><DemoExplainer screen="BLUEPRINT_COCKPIT" /></>);
export default BlueprintCockpitWithExplainer;
